import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaGithub, FaLinkedin, FaFacebook, FaInstagram } from 'react-icons/fa';
import { SiUpwork } from 'react-icons/si';
import { useAppTheme } from '../../context/ThemeContext';

/* eslint-disable @typescript-eslint/no-unsafe-return */
const HeroSection = styled.section`
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr;
  position: relative;
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing.xxxl} ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.background};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.xxl} ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
  }
`;

const LightEffect = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 5;
  background: radial-gradient(
    600px circle at var(--mouse-x, 50vw) var(--mouse-y, 50vh),
    rgba(255, 255, 255, 0.15) 0%,
    transparent 40%
  );
  mix-blend-mode: color-dodge;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const ContentContainer = styled.div`
  max-width: ${({ theme }) => theme.maxWidth};
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: calc(100vh - ${({ theme }) => theme.spacing.xxxl} * 2);
  position: relative;
  z-index: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-height: calc(100vh - ${({ theme }) => theme.spacing.xxl} * 2);
  }
`;

const StatusIndicator = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.75rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
  }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing.xxl};
`;

const TitleBlock = styled.div`
  position: relative;
`;

const Title = styled(motion.h1)`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(4rem, 12vw, 10rem);
  font-weight: 700;
  line-height: 0.9;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
  position: relative;
  letter-spacing: -0.03em;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: clamp(2.5rem, 13vw, 4rem);
    margin-bottom: ${({ theme }) => theme.spacing.xl};
  }
`;

const NameHighlight = styled.span<{ $mode?: string }>`
  color: ${({ theme, $mode }) =>
    $mode === 'light' ? theme.colors.background : theme.colors.primary};
  background-color: ${({ theme, $mode }) =>
    $mode === 'light' ? theme.colors.primary : 'transparent'};
  display: inline-block;
  padding: ${({ $mode }) => ($mode === 'light' ? '0 0.1em' : '0')};
  position: relative;
`;

const Role = styled(motion.div)`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: clamp(1.125rem, 2.5vw, 1.5rem);
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xxxl};
  max-width: 700px;
  line-height: 1.6;
  letter-spacing: -0.01em;
`;

const RoleHighlight = styled.span`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.background};
  padding: 0.1em 0.3em;
  font-weight: 500;
`;

const BottomSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xxxl};
  margin-top: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.xl};
  }
`;

const SocialLinks = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-left: 0;
  }
`;

const SocialLink = styled(motion.a)`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.125rem;
  transition: all 0.2s ease;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 1px solid transparent;
    transition: border-color 0.2s ease;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};

    &::after {
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const ScrollPrompt = styled(motion.div)`
  position: fixed;
  bottom: ${({ theme }) => theme.spacing.xl};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  z-index: 10;
  cursor: pointer;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.7;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

const ScrollText = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.625rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ScrollLine = styled(motion.div)`
  width: 1px;
  height: 40px;
  background: ${({ theme }) => theme.colors.border};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 30%;
    background: ${({ theme }) => theme.colors.primary};
  }
`;

const HiddenFactsContainer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
`;

const HiddenFact = styled.div<{ $x: number; $y: number }>`
  position: absolute;
  left: ${props => props.$x}%;
  top: ${props => props.$y}%;
  transform: translate(-50%, -50%);
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 2.25rem;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  user-select: none;
  font-weight: 600;
  pointer-events: none;
  will-change: color, opacity, text-shadow;
  max-width: 80vw;
  white-space: normal;
  word-break: keep-all;
  word-spacing: 0.2em;
  line-height: 1.4;
  text-align: center;
  overflow-wrap: break-word;
  padding: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.875rem;
    max-width: 75vw;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: none;
  }
`;

const DiscoverHint = styled(motion.div)`
  position: fixed;
  bottom: 120px;
  left: 50%;
  transform: translateX(-50%);
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.625rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: 0.5;
  z-index: 10;
  pointer-events: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: none;
  }
`;

const taglines = [
  {
    normal: 'I make the worst UI ever, so',
    highlight: 'users will always remember your brand.',
  },
  {
    normal: 'I write bugs professionally, then',
    highlight: 'ship rock-solid production code.',
  },
  {
    normal: 'I specialize in making apps crash, so',
    highlight: 'I know exactly how to prevent it.',
  },
  {
    normal: 'I design the slowest interfaces, which taught me',
    highlight: 'how to optimize for lightning-fast performance.',
  },
  {
    normal: "I create apps nobody wants to use, that's why",
    highlight: 'I obsess over perfect user experience.',
  },
  {
    normal: 'I overcomplicate everything, so now',
    highlight: 'I build elegantly simple solutions.',
  },
  {
    normal: "I ignore best practices religiously, that's how",
    highlight: 'I learned which ones actually matter.',
  },
  {
    normal: 'I built features nobody asked for, now',
    highlight: 'I ship exactly what drives business value.',
  },
  {
    normal: 'I made apps that looked beautiful but broke, so',
    highlight: 'I balance aesthetics with bulletproof functionality.',
  },
  {
    normal: 'I deploy on Fridays at 5 PM, which taught me',
    highlight: 'how to implement fail-safe CI/CD pipelines.',
  },
  {
    normal: "I hardcode everything in production, that's why",
    highlight: 'I now architect scalable, maintainable systems.',
  },
  {
    normal: 'I write code only I can understand, so',
    highlight: 'I document everything for team success.',
  },
  {
    normal: 'I ignore mobile users completely, which taught me',
    highlight: 'to prioritize mobile-first responsive design.',
  },
  {
    normal: "I reinvent the wheel daily, that's why",
    highlight: 'I leverage proven frameworks efficiently.',
  },
  {
    normal: 'I skip testing entirely, so now',
    highlight: 'I write comprehensive tests that save time.',
  },
  {
    normal: 'I make apps that drain batteries in minutes, now',
    highlight: 'I optimize for maximum performance and efficiency.',
  },
  {
    normal: 'I built features that took 10 seconds to load, so',
    highlight: 'I obsess over sub-second response times.',
  },
  {
    normal: "I create inconsistent designs everywhere, that's why",
    highlight: 'I build cohesive component libraries.',
  },
  {
    normal: 'I ignore accessibility completely, which taught me',
    highlight: 'how to build inclusive apps for everyone.',
  },
  {
    normal: 'I never finish anything on time, so now',
    highlight: 'I deliver reliable results that exceed deadlines.',
  },
  // 50 NEW ADDITIONS
  {
    normal: 'I delete more code than I write, which means',
    highlight: "I'm extremely efficient at reducing technical debt.",
  },
  {
    normal: 'I automate myself out of jobs, so',
    highlight: "you'll never pay me to do the same task twice.",
  },
  {
    normal: 'I turn coffee into code, and then',
    highlight: 'turn that code into enterprise solutions.',
  },
  {
    normal: 'I break production on a regular basis, which is how',
    highlight: 'I learn to build resilient architectures.',
  },
  {
    normal: 'I spend 90% of my time reading code, so',
    highlight: 'I can spend the other 10% writing the exact right lines.',
  },
  {
    normal: 'I have strong opinions loosely held, meaning',
    highlight: "I'll fight for best practices but pivot when data proves me wrong.",
  },
  {
    normal: 'I refuse to use the latest hyped framework, because',
    highlight: 'boring technology usually ships faster and breaks less.',
  },
  {
    normal: 'I write code that looks painfully simple, which takes',
    highlight: 'hours of ruthless refactoring to achieve.',
  },
  {
    normal: 'I assume every user will try to break the system, so',
    highlight: 'I build zero-trust security from the ground up.',
  },
  {
    normal: 'I optimize for developers deleting my code later, which means',
    highlight: 'my architecture is completely decoupled and modular.',
  },
  {
    normal: 'I treat warnings as errors, so',
    highlight: 'my build pipelines stay absolutely pristine.',
  },
  {
    normal: 'I ask too many questions during sprint planning, so',
    highlight: "we don't waste weeks building the wrong features.",
  },
  {
    normal: 'I prefer a slow CI pipeline over a fast one, if it means',
    highlight: 'preventing catastrophic regressions from reaching users.',
  },
  {
    normal: "I design databases like we'll have a million users tomorrow, but",
    highlight: 'provision infrastructure for the traffic we have today.',
  },
  {
    normal: 'I write documentation before I write code, which is why',
    highlight: 'my APIs are instinctively intuitive.',
  },
  {
    normal: "I consider a feature incomplete until it's monitored, so",
    highlight: 'I know it broke before the users do.',
  },
  {
    normal: 'I hate complex state management, which naturally leads to',
    highlight: 'unbelievably crisp and predictable user interfaces.',
  },
  {
    normal: 'I stubbornly stick to keyboard shortcuts, because',
    highlight: 'mouse-driven development is too slow for my delivery cycles.',
  },
  {
    normal: 'I assume every API call will fail, which is exactly why',
    highlight: 'my frontends never leave users staring at blank screens.',
  },
  {
    normal: 'I aggressively limit my work-in-progress, resulting in',
    highlight: 'a devastatingly high sprint completion rate.',
  },
  {
    normal: 'I view every third-party dependency as a liability, which keeps',
    highlight: 'my bundle sizes microscopic and secure.',
  },
  {
    normal: 'I spend hours naming variables, because',
    highlight: "code is read a hundred times more often than it's written.",
  },
  {
    normal: 'I build features assuming the network is 3G, so',
    highlight: 'they feel instantaneously fast on broadband.',
  },
  {
    normal: 'I actively try to convince clients not to build things, saving them',
    highlight: "time and money on features they don't actually need.",
  },
  {
    normal: 'I write commit messages like a novelist, because',
    highlight: 'future debuggers deserve a good story.',
  },
  {
    normal: 'I consider pixel-perfect design a bare minimum, while',
    highlight: 'obsessing over accessibility and semantic markup.',
  },
  {
    normal: 'I rewrite my own components weekly, constantly',
    highlight: 'chasing the perfect abstraction boundary.',
  },
  {
    normal: 'I treat my local environment like production, so',
    highlight: '"works on my machine" means it works everywhere.',
  },
  {
    normal: 'I let users discover features intuitively, because',
    highlight: 'nobody actually reads the instruction manual.',
  },
  {
    normal: 'I debug by explaining my code to an inanimate object, and',
    highlight: 'the rubber duck is surprisingly insightful.',
  },
  {
    normal: 'I embrace constructive laziness, meaning I will',
    highlight: 'spend three days automating a five-minute task.',
  },
  {
    normal: 'I ship early and often, because',
    highlight: 'the only real validation comes from actual users.',
  },
  {
    normal: "I don't just fix the symptom, I",
    highlight: 'hunt down and eradicate the root cause.',
  },
  {
    normal: 'I design APIs for other developers, so',
    highlight: 'they never have to guess the payload structure.',
  },
  {
    normal: 'I ruthlessly prune dead code, keeping',
    highlight: 'the codebase as lean and sharp as a scalpel.',
  },
  {
    normal: 'I anticipate scale issues before they happen, letting',
    highlight: 'the business grow without technical ceilings.',
  },
  {
    normal: 'I read the documentation fully, so',
    highlight: "you don't have to spend hours on Stack Overflow.",
  },
  {
    normal: 'I embrace constraints creatively, turning',
    highlight: 'limitations into defining product features.',
  },
  {
    normal: 'I prototype with disposable code, guaranteeing',
    highlight: 'we build the right thing before we build it right.',
  },
  {
    normal: 'I log errors with obsessive detail, turning',
    highlight: 'post-mortems into precise surgical strikes.',
  },
  {
    normal: 'I structure projects logically, ensuring',
    highlight: 'new hires can navigate it on their first day.',
  },
  {
    normal: 'I view caching as a last resort, prioritizing',
    highlight: 'fundamentally performant database queries.',
  },
  {
    normal: 'I embrace code reviews as a learning tool, checking',
    highlight: 'egos at the door for the sake of the product.',
  },
  {
    normal: 'I modularize monolithic structures, enabling',
    highlight: 'teams to deploy independently without friction.',
  },
  {
    normal: 'I script my infrastructure deployments, ensuring',
    highlight: 'servers are Cattle, not Pets.',
  },
  {
    normal: 'I write idempotent scripts, meaning',
    highlight: 're-running them is always safe and predictable.',
  },
  {
    normal: 'I champion dark deployments, so',
    highlight: 'features are battle-tested before the big reveal.',
  },
  {
    normal: 'I focus on graceful degradation, ensuring',
    highlight: 'the core experience survives when services fail.',
  },
  {
    normal: 'I measure everything that moves, because',
    highlight: 'what gets measured gets meaningfully optimized.',
  },
  {
    normal: 'I balance perfection with pragmatism, knowing',
    highlight: 'shipping is ultimately a feature.',
  },
];

const Hero: React.FC = () => {
  const { mode } = useAppTheme();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showScroll, setShowScroll] = useState(true);
  const [discoveredFacts, setDiscoveredFacts] = useState<Set<number>>(new Set());
  const [randomTagline] = useState(() => taglines[Math.floor(Math.random() * taglines.length)]);

  const allFacts = [
    'Born in Ha Tinh, Vietnam',
    '27 Years Old',
    'Living in Ha Noi',
    'Love to discover new things',
    'Kindness & Humbleness matter',
    'I love mountain than sea',
    'Attitude is more important',
    'Never rude for older people',
    'I got shortsighted',
    'Want to travel to Japan and China',
    'Love to hear negative feedback to improve',
  ];

  const [hiddenFacts] = useState(() => {
    // Shuffle and select 5 random facts
    const shuffled = [...allFacts].sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, 5);

    // Generate random positions with safe margins from edges
    const positions: Array<{ text: string; x: number; y: number }> = [];
    const usedPositions: Array<{ x: number; y: number }> = [];

    // Safe margins to prevent edge cutoff (10% from each edge)
    const marginX = 10;
    const marginY = 10;
    const minX = marginX;
    const maxX = 100 - marginX;
    const minY = marginY;
    const maxY = 100 - marginY;

    // Main content exclusion zone (center area where title and text appear)
    const contentZone = {
      xMin: 15,
      xMax: 70,
      yMin: 25,
      yMax: 75,
    };

    const isInContentZone = (x: number, y: number) => {
      return (
        x >= contentZone.xMin &&
        x <= contentZone.xMax &&
        y >= contentZone.yMin &&
        y <= contentZone.yMax
      );
    };

    const isPositionValid = (x: number, y: number, minDistance = 12) => {
      // 80% chance to avoid content zone, 20% chance to allow overlap
      const avoidContent = Math.random() > 0.2;
      if (avoidContent && isInContentZone(x, y)) {
        return false;
      }

      return usedPositions.every(pos => {
        const distance = Math.sqrt(Math.pow(pos.x - x, 2) + Math.pow(pos.y - y, 2));
        return distance > minDistance;
      });
    };

    selected.forEach(text => {
      let x, y;
      let attempts = 0;
      do {
        // Random position with safe margins from edges
        x = minX + Math.random() * (maxX - minX);
        y = minY + Math.random() * (maxY - minY);
        attempts++;
      } while (!isPositionValid(x, y) && attempts < 150);

      positions.push({ text, x, y });
      usedPositions.push({ x, y });
    });

    return positions;
  });
  const getDistanceFromMouse = (factX: number, factY: number) => {
    const factPosX = (window.innerWidth * factX) / 100;
    const factPosY = (window.innerHeight * factY) / 100;
    const distance = Math.sqrt(
      Math.pow(mousePosition.x - factPosX, 2) + Math.pow(mousePosition.y - factPosY, 2)
    );
    return distance;
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Use requestAnimationFrame for smoother updates
      requestAnimationFrame(() => {
        setMousePosition({
          x: e.clientX,
          y: e.clientY,
        });
      });
    };

    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowScroll(false);
      } else {
        setShowScroll(true);
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToNextSection = () => {
    const heroSection = document.getElementById('hero');
    if (heroSection) {
      const nextSection = heroSection.nextElementSibling;
      if (nextSection) {
        nextSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <HeroSection
      id="hero"
      style={
        {
          '--mouse-x': `${mousePosition.x}px`,
          '--mouse-y': `${mousePosition.y}px`,
        } as React.CSSProperties
      }
    >
      {mode === 'dark' && <LightEffect />}
      <HiddenFactsContainer>
        {hiddenFacts.map((fact, index) => {
          const distance = getDistanceFromMouse(fact.x, fact.y);
          const revealRadius = 300;
          const isDiscovered = discoveredFacts.has(index);

          // Mark as discovered when cursor is nearby
          if (distance < revealRadius && !isDiscovered) {
            setDiscoveredFacts(prev => {
              const newSet = new Set(prev);
              newSet.add(index);
              return newSet;
            });
          }

          let color, opacity, textShadow;

          // If discovered, keep it visible permanently, brighten if cursor is close
          if (isDiscovered) {
            // Always show discovered facts - never disappear
            const intensity = Math.max(0, 1 - distance / revealRadius);
            const adjustedIntensity = Math.pow(intensity, 1.8);
            const baseValue = Math.floor(80 + adjustedIntensity * 140);
            const value = baseValue;
            color = `rgb(${value}, ${value}, ${value - 5})`;
            opacity = Math.max(0.45, adjustedIntensity * 0.95);
            const glow = Math.max(8, adjustedIntensity * 20);
            textShadow = `0 0 ${glow}px rgba(100, 255, 218, ${Math.max(0.2, adjustedIntensity * 0.7)})`;
          } else {
            // Not discovered yet - only visible when cursor is nearby
            if (distance < revealRadius) {
              const intensity = 1 - distance / revealRadius;
              const adjustedIntensity = Math.pow(intensity, 1.8);
              const baseValue = 15;
              const range = 205;
              const value = Math.floor(baseValue + adjustedIntensity * range);
              color = `rgb(${value}, ${value}, ${value - 5})`;
              opacity = adjustedIntensity * 0.95;
              const glow = adjustedIntensity * 20;
              textShadow = `0 0 ${glow}px rgba(100, 255, 218, ${adjustedIntensity * 0.7})`;
            } else {
              // Cursor is far away - completely hidden
              color = 'rgb(15, 15, 15)';
              opacity = 0;
              textShadow = 'none';
            }
          }

          return (
            <HiddenFact
              key={index}
              $x={fact.x}
              $y={fact.y}
              style={{
                color,
                opacity,
                textShadow,
                transition: 'color 0.2s ease-out, opacity 0.2s ease-out, text-shadow 0.2s ease-out',
              }}
            >
              {fact.text}
            </HiddenFact>
          );
        })}
      </HiddenFactsContainer>{' '}
      <DiscoverHint
        initial={{ opacity: 0 }}
        animate={{ opacity: showScroll ? 0.5 : 0 }}
        transition={{ duration: 0.3 }}
        style={{ pointerEvents: 'none' }}
      >
        Move to discover
      </DiscoverHint>
      <ContentContainer>
        <MainContent>
          <StatusIndicator
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Available for work
          </StatusIndicator>

          <TitleBlock>
            <Title
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <NameHighlight $mode={mode}>Lê Quốc Khánh</NameHighlight>
            </Title>
            <Role
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {randomTagline.normal} <RoleHighlight>{randomTagline.highlight}</RoleHighlight>
            </Role>
          </TitleBlock>

          <BottomSection>
            <SocialLinks
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <SocialLink
                href="https://github.com/khanh21011999"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaGithub />
              </SocialLink>
              <SocialLink
                href="https://www.linkedin.com/in/l%C3%AA-qu%E1%BB%91c-kh%C3%A1nh-364543231/"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaLinkedin />
              </SocialLink>
              <SocialLink
                href="https://www.facebook.com/quockhanh21021999/"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaFacebook />
              </SocialLink>
              <SocialLink
                href="https://www.instagram.com/without_jklmnopqrst/"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaInstagram />
              </SocialLink>
              <SocialLink
                href="https://www.upwork.com/freelancers/~019f04d8ea920eb2a0"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <SiUpwork />
              </SocialLink>
            </SocialLinks>
          </BottomSection>
        </MainContent>

        <ScrollPrompt
          initial={{ opacity: 0 }}
          animate={{ opacity: showScroll ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          onClick={scrollToNextSection}
          style={{ pointerEvents: showScroll ? 'auto' : 'none' }}
        >
          <ScrollText>Scroll</ScrollText>
          <ScrollLine
            animate={{
              scaleY: [1, 1.5, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </ScrollPrompt>
      </ContentContainer>
    </HeroSection>
  );
};

export default Hero;
